import { useEffect, useMemo, useState } from 'react'
import type { CheckoutSavedAddress } from '../addressBookAdapter'
import type { CheckoutAddressForm } from '../flow'
import {
  buildAddressFormSignature,
  buildEmptyDraftAddress,
  hasRequiredCheckoutAddressFields,
  isEmptyCheckoutAddressForm,
  mergeCustomCheckoutAddresses,
} from './addressStepHelpers'

type UseCheckoutAddressPanelInput = {
  customAddress: {
    defaultBilling: boolean
    defaultShipping: boolean
    idPrefix: string
    label: string
    useBilling: boolean
    useShipping: boolean
  }
  enabled?: boolean
  form: CheckoutAddressForm
  missingFieldsMessage: string
  onSetForm: (value: CheckoutAddressForm) => void
  savedAddresses: CheckoutSavedAddress[]
  selectSavedAddress: (address: CheckoutSavedAddress) => boolean
}

export const useCheckoutAddressPanel = ({
  customAddress,
  enabled = true,
  form,
  missingFieldsMessage,
  onSetForm,
  savedAddresses,
  selectSavedAddress,
}: UseCheckoutAddressPanelInput) => {
  const [customAddresses, setCustomAddresses] = useState<CheckoutSavedAddress[]>([])
  const [isFormPanelOpen, setFormPanelOpen] = useState(() => enabled && (savedAddresses.length === 0 || !isEmptyCheckoutAddressForm(form)))
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null)
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)
  const [panelError, setPanelError] = useState('')

  const addresses = useMemo(() => {
    const base = savedAddresses.filter(selectSavedAddress)
    return mergeCustomCheckoutAddresses(base, customAddresses)
  }, [customAddresses, savedAddresses, selectSavedAddress])

  useEffect(() => {
    if (!enabled) {
      setSelectedAddressId(null)
      setEditingAddressId(null)
      setFormPanelOpen(false)
      return
    }

    if (addresses.length === 0) {
      setFormPanelOpen(true)
      return
    }
    if (!isEmptyCheckoutAddressForm(form) && !selectedAddressId) {
      setFormPanelOpen(true)
    }
  }, [addresses.length, enabled, form, selectedAddressId])

  const normalizedFormSignature = useMemo(() => buildAddressFormSignature(form), [form])

  useEffect(() => {
    if (!enabled) return
    const match = addresses.find((address) => buildAddressFormSignature(address.address) === normalizedFormSignature)
    setSelectedAddressId(match?.id ?? null)
  }, [addresses, enabled, normalizedFormSignature])

  const selectAddress = (address: CheckoutSavedAddress) => {
    setPanelError('')
    setSelectedAddressId(address.id)
    onSetForm(address.address)
    setEditingAddressId(null)
    setFormPanelOpen(false)
  }

  const editAddress = (address: CheckoutSavedAddress) => {
    setPanelError('')
    setSelectedAddressId(address.id)
    setEditingAddressId(address.id)
    onSetForm(address.address)
    setFormPanelOpen(true)
  }

  const addNewAddress = () => {
    setPanelError('')
    setSelectedAddressId(null)
    setEditingAddressId(null)
    onSetForm(buildEmptyDraftAddress(form))
    setFormPanelOpen(true)
  }

  const submitPanelAddress = () => {
    setPanelError('')
    if (!hasRequiredCheckoutAddressFields(form)) {
      setPanelError(missingFieldsMessage)
      return
    }

    const id = editingAddressId ?? `${customAddress.idPrefix}-${Date.now()}`
    const nextAddress: CheckoutSavedAddress = {
      id,
      label: customAddress.label,
      address: form,
      useShipping: customAddress.useShipping,
      useBilling: customAddress.useBilling,
      defaultShipping: customAddress.defaultShipping,
      defaultBilling: customAddress.defaultBilling,
    }
    setCustomAddresses((current) => {
      const index = current.findIndex((entry) => entry.id === id)
      if (index === -1) return [...current, nextAddress]
      const copy = [...current]
      copy[index] = nextAddress
      return copy
    })
    setSelectedAddressId(id)
    setEditingAddressId(null)
    setFormPanelOpen(false)
  }

  const cancelPanelAddress = () => {
    setPanelError('')
    setEditingAddressId(null)
    const selected = addresses.find((entry) => entry.id === selectedAddressId)
    if (selected) {
      onSetForm(selected.address)
      setFormPanelOpen(false)
      return
    }
    if (addresses.length > 0) {
      setFormPanelOpen(false)
    }
  }

  return {
    addNewAddress,
    addresses,
    cancelPanelAddress,
    editAddress,
    editingAddressId,
    isFormPanelOpen,
    panelError,
    selectAddress,
    selectedAddressId,
    submitPanelAddress,
  }
}
